import { useState } from "react";
import { router } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { ProgramWindow } from "@/interfaces/program";
import { formatWindow } from "@/utils/programWindow";

// Where an event stands in the October move, and the one action it needs next.
//
// Three states, and only one of them asks a question:
//
//  - `reschedule_pending`  a US event still on its original date, organizer
//                          hasn't said whether they're keeping it. Silence
//                          means keep, so this never blocks anything.
//  - `awaiting_new_date`   moved, nothing picked yet. The state that needs
//                          chasing: approved, shareable, unattendable.
//  - moved and re-dated    a quiet line confirming what happened, so an
//                          organizer coming back later isn't confused about
//                          why their date changed.
//
// Renders nothing at all for an event the move never touched.
export default function EventReschedule({
  event,
  canEdit,
  octoberWindow,
  originalWindow,
}: {
  event: SatelliteEvent;
  canEdit: boolean;
  // Passed separately from `event.program_window`. An event that hasn't moved
  // yet still answers to the September window, so it can't describe where it
  // is being invited to go.
  octoberWindow: ProgramWindow;
  // And the mirror of that: a moved event answers to October, so it can't
  // describe the window it would be going back to.
  originalWindow: ProgramWindow;
}) {
  const [submitting, setSubmitting] = useState<
    "keep" | "postpone" | "return" | null
  >(null);

  const windowLabel = event.program_window
    ? formatWindow(event.program_window)
    : "";

  function choose(choice: "keep" | "postpone" | "return") {
    setSubmitting(choice);
    router.post(
      `/organize/${event.id}/reschedule`,
      { choice },
      { onFinish: () => setSubmitting(null), preserveScroll: true },
    );
  }

  // Moving was the organizer's call, so unmoving is too. Offered in both moved
  // states because a venue can say yes before or after a new date is picked,
  // and withheld once the original window has run out of days to go back to.
  const returnOffer =
    canEdit && event.can_return_to_original_window ? (
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          onClick={() => choose("return")}
          disabled={submitting !== null}
        >
          {submitting === "return"
            ? "Moving back..."
            : `Go back to ${formatWindow(originalWindow) || "the original dates"}`}
        </Button>
        <p className="text-caption text-ink-muted">
          Venue came through after all? This clears your date so you can pick
          one in the original window. Everyone keeps their spot, and telling
          them is still down to you.
        </p>
      </div>
    ) : null;

  if (event.reschedule_pending && canEdit) {
    return (
      <Panel title="new-dates.txt" tone="alt">
        <div className="flex flex-col gap-4">
          <p className="text-body text-ink">
            We're moving every Boba Bash outside the US to{" "}
            <strong>{formatWindow(octoberWindow) || "October"}</strong> so
            organizers get more time to get ready.{" "}
            <strong>Your event isn't being moved</strong> - the US is running on
            schedule. But if you'd rather have the extra time, you can come with
            us.
          </p>

          <p className="text-caption text-ink-muted">
            Running as planned on <strong>{event.starts_at_short_label}</strong>
            . You don't have to do anything - this is just so we know.
          </p>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              onClick={() => choose("keep")}
              disabled={submitting !== null}
            >
              {submitting === "keep"
                ? "Saving..."
                : `Keep ${event.starts_at_short_label}`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => choose("postpone")}
              disabled={submitting !== null}
            >
              {submitting === "postpone" ? "Moving..." : "Move to October"}
            </Button>
          </div>

          <p className="text-caption text-ink-muted">
            Moving clears your date so you can pick a new one. Everyone who's
            RSVP'd keeps their spot, and telling them is down to you - we don't
            email your roster.
          </p>
        </div>
      </Panel>
    );
  }

  if (event.awaiting_new_date) {
    return (
      <Panel title="new-dates.txt" tone="alt">
        <div className="flex flex-col gap-3">
          <p className="text-body text-ink">
            <strong>This event needs a new date.</strong>{" "}
            {event.postponed_from_label
              ? `It was ${event.postponed_from_label}, and we've moved the program back to give everyone more time.`
              : "We've moved the program back to give everyone more time."}
          </p>
          {windowLabel && (
            <p className="text-body text-ink">
              Pick a day between <strong>{windowLabel}</strong> in the event
              details below. Check with your venue first - the old booking
              almost certainly doesn't carry over.
            </p>
          )}
          <p className="text-caption text-ink-muted">
            Everyone who already RSVP'd keeps their spot, and your page stays
            live saying a new date is coming.{" "}
            <strong>Telling them is down to you</strong> - we haven't emailed
            your roster, because that news lands better from the person who
            invited them.
          </p>
          {returnOffer}
        </div>
      </Panel>
    );
  }

  if (event.postponed && event.starts_at) {
    return (
      <Panel title="new-dates.txt" tone="alt">
        <div className="flex flex-col gap-3">
          <p className="text-body text-ink">
            This event moved to <strong>{event.starts_at_short_label}</strong>
            {event.postponed_from_label
              ? ` from ${event.postponed_from_label}.`
              : "."}{" "}
            Everyone kept their spot. If you haven't already told them the new
            date, now's the moment - we don't email your roster.
          </p>
          {returnOffer}
        </div>
      </Panel>
    );
  }

  return null;
}
